import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { UserFacadeService } from 'src/app/auth/state/user-facade.service';
import { OrganizationFacadeService } from '../state/organization/organization-facade.service';
import { OrganizationConfigFacadeService } from '../state/organization-config/organization-config-facade.service';
import { CountryCodeFacadeService } from '../state/country-code/country-code-facade.service';
import { OrderEditPersistenceService } from 'src/app/procurement/state/order-edit/order-edit-persistence.service';

export interface AppInitResult {
  userInitialized: boolean;
  orgInitialized: boolean;
  configInitialized: boolean;
  countryCodeInitialized: boolean;
}

/**
 * Master resolver that initializes all core app state in the correct order:
 * 1. User (from Firebase Auth)
 * 2. Organization (based on user's orgId)
 * 3. Organization Config (based on organization)
 * 4. Country Code (based on organization)
 */
export const appInitResolver: ResolveFn<AppInitResult> = async (route, state) => {
  console.log('🚀 [AppInitResolver] Starting application initialization...');

  const userFacade = inject(UserFacadeService);
  const organizationFacade = inject(OrganizationFacadeService);
  const configFacade = inject(OrganizationConfigFacadeService);
  const countryCodeFacade = inject(CountryCodeFacadeService);
  const orderEditPersistence = inject(OrderEditPersistenceService);

  // Cleanup old order drafts from localStorage (older than 7 days)
  orderEditPersistence.cleanupOldDrafts();

  const result: AppInitResult = {
    userInitialized: false,
    orgInitialized: false,
    configInitialized: false,
    countryCodeInitialized: false,
  };

  try {
    // Step 1: Initialize user (should already be done by AuthGuard, but ensure it's complete)
    console.log('👤 [AppInitResolver] Step 1: Checking user...');
    const user = userFacade.getCurrentUser();

    if (!user) {
      console.warn('⚠️ [AppInitResolver] No user found - user should be authenticated');
      return result;
    }

    result.userInitialized = true;
    console.log('✅ [AppInitResolver] User ready:', user.id);

    // Step 2: Initialize organization
    console.log('🏢 [AppInitResolver] Step 2: Initializing organization...');
    result.orgInitialized = await organizationFacade.initialize(user.id, user.orgId, user.orgIds);

    if (!result.orgInitialized) {
      console.warn('⚠️ [AppInitResolver] Organization initialization failed');
      return result;
    }

    const org = organizationFacade.getOrganization();
    console.log('✅ [AppInitResolver] Organization ready:', { id: org.id, name: org.name });

    // Step 3: Initialize organization config (runs in parallel with country code)
    console.log('⚙️ [AppInitResolver] Step 3: Initializing config and country code...');

    const [configSuccess, countryCodeSuccess] = await Promise.all([
      configFacade.initialize(org.id),
      countryCodeFacade.initialize(),
    ]);

    result.configInitialized = configSuccess;
    result.countryCodeInitialized = countryCodeSuccess;

    if (configSuccess) {
      console.log('✅ [AppInitResolver] Config ready');
    } else {
      console.warn('⚠️ [AppInitResolver] Config initialization failed');
    }

    if (countryCodeSuccess) {
      console.log('✅ [AppInitResolver] Country code ready:', countryCodeFacade.getOrgCountryCode());
    } else {
      console.warn('⚠️ [AppInitResolver] Country code initialization failed');
    }

    console.log('🎉 [AppInitResolver] Application initialization complete!', result);
    return result;
  } catch (error) {
    console.error('🚨 [AppInitResolver] Error during initialization', error);
    return result;
  }
};
